import React from 'react';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { Box, Center, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { Notification } from 'clipsal-cortex-types/src/api/api-notifications';

import { NotificationListItem } from './NotificationListItem';
import { useNotificationSearchMatcher } from './notifications-helper';
import { NotificationGrouping, useGetNotifications } from './notificationsApi';
import { NotificationsListSkeletonLoader } from './NotificationsListSkeletonLoader';

type Props = {
  groupBy: NotificationGrouping;
  // The query limit — grows by a page each time "Load more" is clicked
  limit?: number;
  // Only notifications whose title matches this search term are fetched
  searchQuery?: string;
  isArchived?: boolean;
  isEditMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (notificationId: string) => void;
  onSwipeSelect?: (notificationId: string) => void;
};

export function NotificationsListPriorityStatus({
  groupBy,
  limit = 10,
  searchQuery = '',
  isArchived = false,
  isEditMode = false,
  selectedIds,
  onToggleSelect,
  onSwipeSelect,
}: Props) {
  const textColor = useColorModeValue('#111111', '#EEEEEE');
  const {
    data: { alarm, info, warning },
    isLoading,
    isFetching,
    isError,
  } = useGetNotifications(groupBy, { limit, offset: 0, archived: isArchived, search: searchQuery || undefined });
  const { t } = useTranslation();
  const matchesSearch = useNotificationSearchMatcher(searchQuery);
  // Filter by title client-side as well, so search works even if the API ignores the `search` param
  const fetchedAlarmNotifications = alarm?.data?.results;
  const alarmNotifications = fetchedAlarmNotifications?.filter(matchesSearch);
  const totalNumAlarmNotifications = alarm?.data?.count ?? 0;
  const fetchedInfoNotifications = info?.data?.results;
  const infoNotifications = fetchedInfoNotifications?.filter(matchesSearch);
  const totalNumInfoNotifications = info?.data?.count ?? 0;
  const fetchedWarningNotifications = warning?.data?.results;
  const warningNotifications = fetchedWarningNotifications?.filter(matchesSearch);
  const totalNumWarningNotifications = warning?.data?.count ?? 0;

  // True while a larger page is in flight after growing the limit (but not during background polling)
  function isFetchingMore(notifications: Notification[] | undefined, totalCount: number) {
    return isFetching && !!notifications && notifications.length < limit && notifications.length < totalCount;
  }

  function toListItems(notifications: Notification[] | undefined) {
    return isLoading ? (
      <NotificationsListSkeletonLoader />
    ) : (
      notifications?.map((notification) => (
        <NotificationListItem
          notification={notification}
          key={notification.notification_id}
          isArchived={isArchived}
          isEditMode={isEditMode}
          isSelected={selectedIds?.has(notification.notification_id)}
          onToggleSelect={onToggleSelect}
          onSwipeSelect={onSwipeSelect}
        />
      ))
    );
  }

  if (isError) {
    return (
      <Alert status="error" my={4}>
        <AlertIcon w={8} h={8} />
        <AlertDescription py={4}>
          {t('Notifications.error fetching notifications')}.{' '}
          {`${t('Common.please try again')} ${t('Common.if this persists contact support')}`}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      <Text data-testid="list-type-title-alarm" color={textColor}>
        {t('Notifications.alarm').toUpperCase()}
      </Text>
      <Box data-testid="notifications-list-alarm" mb={3}>
        {!alarmNotifications?.length && !isLoading ? (
          <Center data-testid="no-alarm-notifications" my={4}>
            <Text>{t('Notifications.no type notifications', { type: 'alarm' })}.</Text>
          </Center>
        ) : (
          toListItems(alarmNotifications)
        )}

        {isFetchingMore(fetchedAlarmNotifications, totalNumAlarmNotifications) && (
          <NotificationsListSkeletonLoader numItems={2} />
        )}
      </Box>
      <Text data-testid="list-type-title-warning" color={textColor}>
        {t('Notifications.warning').toUpperCase()}
      </Text>
      <Box data-testid="notifications-list-warning" mb={3}>
        {!warningNotifications?.length && !isLoading ? (
          <Center data-testid="no-warning-notifications" my={4}>
            <Text>{t('Notifications.no type notifications', { type: 'warning' })}.</Text>
          </Center>
        ) : (
          toListItems(warningNotifications)
        )}

        {isFetchingMore(fetchedWarningNotifications, totalNumWarningNotifications) && (
          <NotificationsListSkeletonLoader numItems={2} />
        )}
      </Box>
      <Text data-testid="list-type-title-info" color={textColor}>
        {t('Notifications.info').toUpperCase()}
      </Text>
      <Box data-testid="notifications-list-info">
        {!infoNotifications?.length && !isLoading ? (
          <Center data-testid="no-info-notifications" my={4}>
            <Text>{t('Notifications.no type notifications', { type: 'info' })}.</Text>
          </Center>
        ) : (
          toListItems(infoNotifications)
        )}

        {isFetchingMore(fetchedInfoNotifications, totalNumInfoNotifications) && (
          <NotificationsListSkeletonLoader numItems={2} />
        )}
      </Box>
    </Box>
  );
}
